import {
	Branch,
	LLCPriority,
	LLCTransactionData,
	LLCInterestRate,
	LLCMortgage,
	LLCText,
	LLCRegistrationInstructions,
	LLCTransaction,
	AdvancedAmountLabels,
} from '@models/llc-mortgages';
import { InterestOptions, MatterTitle } from '@models/matter';
import {
	InterestRateType,
	MatterMortgage,
	MatterMortgageInstructionSource,
	MatterMortgagePriority,
	MatterMortgageType,
	NetAdvancedAmount,
	NetAmountType,
	TransactionType,
} from '@models/mortgage';
import { MortgageInstructionPerson } from '@models/mortgages/mortgage-base';
import { GetPrimaryTitle } from './matter';
import { uniq } from 'lodash';
import { format, parseISO } from 'date-fns';
import { DATE_FORMAT } from '@models/businesses';
import { isSet, sanitizePropertyValues } from '@core/util/value';
import { dollarsToCents, stringToDollars } from '@core/util/money';
import { createId } from './file';
import { convertLLCAddressToCloserAddress } from './address';
import {
	CorrespondenceOptions,
	CorrespondenceType,
	FinancialInstitutionTypes,
	LenderData,
	MortgageeType,
} from '@models/lender';
import { ClientType } from '@models/clients';

export interface PrefilledNewClient {
	firstName: string;
	middleNames: string;
	lastName: string;
	type: ClientType;
	name: string;
	phone: string;
	workPhone: string;
}

export const getPrefilledNewClient = (
	llcPerson: MortgageInstructionPerson,
	clientType: ClientType,
): PrefilledNewClient => {
	return {
		firstName: llcPerson?.firstName,
		middleNames: llcPerson?.middleName,
		lastName: llcPerson?.lastName,
		type: clientType,
		name: llcPerson?.companyName,
		phone: llcPerson?.phone,
		workPhone: llcPerson?.businessPhone,
	};
};

export const getClientType = (type: string): ClientType =>
	type?.toLowerCase() === 'person' ? ClientType.PERSON : ClientType.CORPORATION;

export const getLLCClientName = (client: MortgageInstructionPerson) => {
	if (isSet(client?.companyName)) {
		return client.companyName.trim();
	}

	return getName(client);
};

const getName = (client: MortgageInstructionPerson) => {
	if (!isSet(client?.firstName)) {
		return null;
	}

	let name = '';
	if (client?.firstName) {
		name += `${client.firstName} `;
	}
	// in the test data the middle names came through as 'null' string - not sure if its an actual case
	if (
		client?.middleName &&
		client?.middleName?.length > 0 &&
		client.middleName.toLowerCase() !== 'null'
	) {
		name += `${client?.middleName} `;
	}
	if (client?.lastName) {
		name += client?.lastName;
	}

	return name.trim();
};

export const getLLCSigningOfficerName = (client: MortgageInstructionPerson) => {
	return getName(client);
};

export const getPriority = (priority: LLCPriority): MatterMortgagePriority => {
	let _priority: MatterMortgagePriority;
	switch (priority) {
		case 'FIRST':
			_priority = MatterMortgagePriority.PRIORITY_1;
			break;
		case 'SECOND':
			_priority = MatterMortgagePriority.PRIORITY_2;
			break;
		case 'THIRD':
			_priority = MatterMortgagePriority.PRIORITY_3;
			break;
		case 'FOURTH':
			_priority = MatterMortgagePriority.PRIORITY_4;
			break;
		case 'FIFTH':
			_priority = MatterMortgagePriority.PRIORITY_5;
			break;
	}

	return _priority;
};

export const getServiceBranch = (transactionData: LLCTransactionData): Branch =>
	getBranch(transactionData, transactionData?.serviceAddressType);

export const getMailingBranch = (transactionData: LLCTransactionData): Branch =>
	getBranch(transactionData, transactionData?.mailingAddressType);

export const getServiceBranchServiceType = (
	transactionData: LLCTransactionData,
): 'branch' | 'centre' | 'mortgage specialist' =>
	getBranchCentre(transactionData, transactionData?.serviceAddressType);

export const getMailingBranchServiceType = (
	transactionData: LLCTransactionData,
): 'branch' | 'centre' | 'mortgage specialist' =>
	getBranchCentre(transactionData, transactionData?.mailingAddressType);

export const getBranchCentre = (
	transactionData: LLCTransactionData,
	type: 'DEAL SOURCE' | 'MORTGAGE CENTER' | 'OWNING BRANCH',
): 'branch' | 'centre' | 'mortgage specialist' => {
	if (type === 'MORTGAGE CENTER') {
		return 'centre';
	}
	if (type === 'OWNING BRANCH') {
		return 'branch';
	}

	if (type === 'DEAL SOURCE') {
		const dealSourceMS = ['mortgagespecialist', 'ms'];

		if (
			dealSourceMS.includes(
				transactionData?.dealSource?.sourceType?.toLowerCase(),
			)
		) {
			return 'mortgage specialist';
		}
		if (transactionData?.dealSource?.sourceType === 'branch') {
			return 'branch';
		}
	}

	return null;
};

export const getBranch = (
	transactionData: LLCTransactionData,
	addressType: 'DEAL SOURCE' | 'MORTGAGE CENTER' | 'OWNING BRANCH',
): Branch => {
	switch (addressType) {
		case 'DEAL SOURCE':
			return transactionData?.dealSource?.branch;
		case 'MORTGAGE CENTER':
			return transactionData?.mortgageCentre;
		case 'OWNING BRANCH':
			return transactionData?.mortgageOwningBranch;
	}
};

export const llcEstateTypeToCloserType = (
	estateType: string,
): InterestOptions | string => {
	const _estateType = estateType?.toLowerCase()?.trim();
	switch (_estateType) {
		case 'fee simple':
			return InterestOptions.FEES;
		case 'leasehold':
			return InterestOptions.LHOLD;
		default:
			return estateType;
	}
};

export const closerEstateTypeToLLCType = (
	estateType: InterestOptions | string,
): string => {
	switch (estateType) {
		case InterestOptions.FEES:
		case InterestOptions.FEEAS:
		case InterestOptions.SURFRT:
		case InterestOptions.MINERT:
			return 'FEE SIMPLE';
		case InterestOptions.LHOLD:
			return 'LEASEHOLD';
		default:
			return estateType;
	}
};

export const GetRequestForFundsPins = (matterTitles: MatterTitle[]) => {
	const primaryTitle = GetPrimaryTitle(matterTitles);
	const propertyId = primaryTitle?.pinNumber;
	const pins =
		matterTitles?.filter((x) => !!x?.pinNumber)?.map((x) => x.pinNumber) ?? [];
	return uniq([propertyId, ...pins])?.filter((x) => x);
};

export const isMortgageMms = (
	transactionData?: LLCTransactionData,
): boolean => {
	return transactionData?.workflowModelId?.toLowerCase() === 'mms';
};

export const getAmount = (amountNumber: number, amountText: string) => {
	if (!(amountNumber || isSet(amountText))) {
		return 0;
	}
	if (amountNumber) {
		return dollarsToCents(amountNumber);
	}

	if (amountText) {
		return dollarsToCents(stringToDollars(amountText)) ?? null;
	}

	return null;
};

export const getDate = (date: string) =>
	date ? format(parseISO(date), DATE_FORMAT) : null;

export const getDateOrString = (date: string, text: LLCText): string => {
	if (date) {
		return format(new Date(date), DATE_FORMAT);
	} else if (text?.textEn) {
		return text?.textEn;
	}

	return '';
};

export const getInterestRateAbovePrime = (incrementAboveBelowPrime: number) =>
	incrementAboveBelowPrime >= 0;

export const getPrincipleAmount = (mortgage: LLCMortgage): number | null =>
	getAmount(mortgage?.registeredAmount, mortgage?.registeredAmountText?.textEn);

export const getInterestRate = (interestRate: LLCInterestRate): number => {
	const interestRateType = getInterestRateType(interestRate);
	return interestRateType === InterestRateType.FIXED
		? interestRate?.fixedRate
		: Math.abs(interestRate?.incrementAboveBelowPrime);
};

export const getInterestRateAsReg = (
	registrationInstructions: LLCRegistrationInstructions,
): string => {
	if (isSet(registrationInstructions?.increment)) {
		return `prime ${registrationInstructions?.increment}%`;
	}

	if (isSet(registrationInstructions?.interestRate)) {
		return `${registrationInstructions?.interestRate}% per annum`;
	}

	return null;
};

export const getInterestRateType = (
	interestRate: LLCInterestRate,
): InterestRateType =>
	interestRate?.interestRateType?.toLowerCase() === 'fixed'
		? InterestRateType.FIXED
		: InterestRateType.VARIABLE;

export const buildMatterBridgeMortgage = (
	transactionId: string,
	transaction: LLCTransaction,
): Partial<MatterMortgage> => {
	const bridgeLoan = transaction?.data?.bridgeLoan;

	const lenderMappedToCloser = mapLLCMortgageeToCloser(transaction);
	const mms = isMortgageMms(transaction?.data);

	const mortgage: Partial<MatterMortgage> = {
		referenceNumber: transaction?.data?.lenderReferenceNumber,
		dealId: mms ? transaction?.data?.sourceFctUrn : transactionId,
		relatedMortgageId: transactionId,
		type: MatterMortgageType.LLC,
		priority: null,
		transactionType: TransactionType.BRIDGE,
		amount: dollarsToCents(bridgeLoan?.loanAmount),
		loanAmount: dollarsToCents(bridgeLoan?.loanAmount),
		firstPaymentDate: getDate(bridgeLoan?.firstPaymentDate),
		interestAdjustmentDate: getDate(bridgeLoan?.interestAdjustmentDate),
		maturityDate: getDate(bridgeLoan?.maturityDate),
		interestRate: bridgeLoan?.interestRate,
		interestRateType: InterestRateType.VARIABLE,
		mms,
		netAdvancedAmounts: buildBridgeFees(transaction),
		source: MatterMortgageInstructionSource.LLC,
		correspondence: {
			correspondenceType: CorrespondenceType.LENDERS,
			correspondence: CorrespondenceOptions.ALL_LENDERS,
		},
		...lenderMappedToCloser,
	};

	return sanitizePropertyValues(mortgage);
};

export const mapLLCMortgageeToCloser = (
	llcTransaction: LLCTransaction,
): LenderData => {
	const serviceAddress = getServiceBranch(llcTransaction?.data);
	const { lender } = llcTransaction;
	const id = createId();
	return {
		lenders: {
			[id]: {
				id: id,
				institutionNumber: lender.lenderCode,
				name: lender.nameEn,
				shortName: lender.shortName,
				address: convertLLCAddressToCloserAddress(serviceAddress.address),
				branch: null,
				phone: serviceAddress?.phone,
				fax: serviceAddress?.fax,
				email: serviceAddress?.email,
				llcLender: llcTransaction.lender,
				llcMortgagee: llcTransaction.mortgagee,
				mortgageeType: MortgageeType.INSTITUTION,
				type: FinancialInstitutionTypes.GENERAL_FINANCIAL_INSTITUTION,
				mortgageeSubType: 'institution',
			},
		},
	};
};

export const buildBridgeFees = (
	llcTransaction: LLCTransaction,
): NetAdvancedAmount[] => {
	const advances: NetAdvancedAmount[] = [];

	if (llcTransaction?.data?.mortgageLoanFees?.bridgeLoanApplicationFee) {
		advances.push({
			id: createId(),
			description: AdvancedAmountLabels.BRIDGE_LOAN_APP_FEE,
			amount: dollarsToCents(
				llcTransaction?.data?.mortgageLoanFees?.bridgeLoanApplicationFee,
			),
			type: NetAmountType.OTHER,
			calculated: true,
		});
	}

	return advances;
};
